/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { MediaSizes } from "@packages/media";
import {
  getContentDescription,
  getContentTitle,
  useCurrentQueueItem,
} from "@packages/media";
import type { HallowElementProps, TextProps } from "@packages/ui";
import {
  constantSemanticColors,
  numericPercentages,
  numericValues,
  spacing,
  Text,
} from "@packages/ui";
import type { MouseEventHandler } from "react";

import { useMediaClass } from "../../../utils/media.emotion";
import { GoFullscreen } from "../groups/GoFullscreen.emotion";
import { MediaMoreMenu } from "../groups/MediaMoreMenu.emotion";

export type MediaInfoProps = HallowElementProps<"div"> & {
  size: MediaSizes;
  showMenu?: boolean;
  onFullscreen: MouseEventHandler;
  onExitFullscreen: MouseEventHandler;
  onMouseMove: MouseEventHandler;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
};

const containerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexShrink: 1,
  flexWrap: "nowrap",
  overflow: "hidden",
  rowGap: numericValues[0],
  width: numericPercentages[100],
};
const outerStyle: Interpolation = {
  alignItems: "center",
  color: constantSemanticColors.onColorHighest,
  columnGap: spacing.s,
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  justifyContent: "space-between",
  maxWidth: numericPercentages[100],
  overflow: "hidden",
  width: "calc(100vw - 200px)",
};
const collapsedStyle: Interpolation = {
  maxWidth: "340px",
  width: "100%",
};

const getDescriptionProps = (
  size: MediaInfoProps["size"],
): {
  type: TextProps["type"];
  size: TextProps["size"];
} => {
  switch (size) {
    case "collapsed-mobile":
      return { type: "detail", size: "l" };
    case "collapsed-desktop":
      return { type: "title", size: "xs" };
    case "expanded-desktop":
    case "expanded-mobile":
    case "fullscreen-desktop":
    case "fullscreen-mobile":
      return { type: "detail", size: "xl" };
    case "hidden-mobile":
    case "hidden-desktop":
    default:
      return { type: "body", size: "s" };
  }
};

const getTitleProps = (
  size: MediaInfoProps["size"],
): {
  type: TextProps["type"];
  size: TextProps["size"];
} => {
  switch (size) {
    case "collapsed-mobile":
    case "collapsed-desktop":
      return { type: "title", size: "m" };
    case "expanded-mobile":
    case "expanded-desktop":
    case "fullscreen-mobile":
    case "fullscreen-desktop":
      return { type: "title", size: "l" };
    case "hidden-mobile":
    case "hidden-desktop":
    default:
      return { type: "headline", size: "s" };
  }
};

export const MediaInfo = ({
  size,
  showMenu,
  onFullscreen,
  onExitFullscreen,
  onMouseMove,
  onMenuOpen,
  onMenuClose,
  ...props
}: MediaInfoProps) => {
  const { currentItem } = useCurrentQueueItem();
  const title = getContentTitle(currentItem);
  const description = getContentDescription(currentItem);
  const descriptionProps = getDescriptionProps(size);
  const titleProps = getTitleProps(size);
  const mediaClass = useMediaClass();

  return (
    <div
      css={[outerStyle, size === "collapsed-desktop" ? collapsedStyle : null]}
      {...props}
      onMouseMove={onMouseMove}
    >
      <div css={containerStyle}>
        <Text overflow="ellipsis" title={title} {...(titleProps as TextProps)}>
          {title}
        </Text>
        <Text
          overflow="ellipsis"
          title={description}
          {...(descriptionProps as TextProps)}
        >
          {description}
        </Text>
      </div>
      {showMenu ? (
        <MediaMoreMenu
          content={currentItem}
          onOpenChange={(open) => (open ? onMenuOpen() : onMenuClose())}
        />
      ) : null}
      {mediaClass.includes("video") &&
      (mediaClass.includes("expanded") || mediaClass.includes("fullscreen")) &&
      (mediaClass.includes("desktop") || mediaClass.includes("landscape")) ? (
        <GoFullscreen
          onFullscreen={onFullscreen}
          onExitFullscreen={onExitFullscreen}
        />
      ) : null}
    </div>
  );
};
